import React, { useState, useEffect, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import operationAPI from '../../api/operationApi';
import useLocationStore from '../../store/locatonStore';
import { encodeDateForUrl } from '../../utils/dateUtils';
import ExitFullScreenIcon from '../../assets/ExitScreenIcon';
import FullScreenIcon from '../../assets/FullScreenIcon';

const API_KEY: string = process.env.REACT_APP_API_URL ?? '';

interface GoogleMapComponentProps {
  date: Date;
}

interface Location {
  latitude: number | null;
  longitude: number | null;
  max: number;
  pickup: string;
  dropOff: string;
  name: string;
  paxNo: number;
}

interface PopupPosition {
  x: number;
  y: number;
}

const GoogleMapComponent: React.FC<GoogleMapComponentProps> = ({ date }) => {
  const [mapCenter, setMapCenter] = useState({ lat: -16.9176, lng: 145.7783 });
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(
    null,
  );
  const [popupPosition, setPopupPosition] = useState<PopupPosition | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { selectedLocation: storedLocation } = useLocationStore();
  const mapRef = useRef<google.maps.Map | null>(null);

  const mapContainerStyle = {
    width: '100%',
    height: '100%',
  };

  const mapOptions = {
    disableDefaultUI: false,
    clickableIcons: false,
    fullscreenControl: false,
  };

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: API_KEY,
  });

  useEffect(() => {
    if (isLoaded) {
      fetchCoordinates(storedLocation.OwnerId, encodeDateForUrl(date));
    }
  }, [isLoaded, storedLocation.OwnerId,date]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const fetchCoordinates = async (ownerId: number, date: string) => {
    try {
            setIsLoading(true);
      const response = await operationAPI.googleMap(ownerId, date);
      const data = response.data;
      if (data && data.length > 0) {
        setLocations(data);
        setMapCenter({
          lat: data[0]?.latitude ?? -16.9176,
          lng: data[0]?.longitude ?? 145.7783,
        });
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching coordinates:', error);
    }
  };

  const calculatePopupPosition = (location: Location) => {
    console.log('calculate location');
    if (!mapRef.current) return null;

    const map = mapRef.current;
    const bounds = map.getBounds();
    const zoom = map.getZoom();

    if (!bounds || !zoom) return null;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const mapDiv = map.getDiv();
    const mapWidth = mapDiv.offsetWidth;
    const mapHeight = mapDiv.offsetHeight;
    const lat = location.latitude ?? -16.9176;
    const lng = location.longitude ?? 145.7783;
    // Calculate position as percentage of map width/height
    const longitudePercent = (lng - sw.lng()) / (ne.lng() - sw.lng());
    const latitudePercent = (ne.lat() - lat) / (ne.lat() - sw.lat());

    return {
      x: mapWidth * longitudePercent,
      y: mapHeight * latitudePercent,
    };
  };

  const handleMarkerClick = (location: Location) => {
    console.log('handle mark location here ');
    const position = calculatePopupPosition(location);
    if (position) {
      setPopupPosition(position);
      setSelectedLocation(location);
    }
  };

  const handleClosePopup = () => {
    setSelectedLocation(null);
    setPopupPosition(null);
  };

  const toggleFullscreen = () => {
    const mapElement = document.getElementById('map-container');
    handleClosePopup();
    if (!document.fullscreenElement) {
      mapElement?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
  <>
    <div
      id="map-container"
      className={`relative ${isFullscreen ? 'w-screen h-screen' : 'h-80'} rounded-lg`}
    >
      {/* Loader Overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          <span className="ml-4 text-blue-600 font-medium">
            Loading coordinates...
          </span>
        </div>
      )}

      {/* Fullscreen Toggle Button */}
      <button
        onClick={toggleFullscreen}
        className="absolute top-4 right-4 z-10 bg-white px-4 py-2 rounded-md shadow-md hover:bg-gray-100 transition-colors duration-200 text-sm font-medium"
      >
        {isFullscreen ? <ExitFullScreenIcon /> : <FullScreenIcon />}
      </button>

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        options={mapOptions}
        zoom={10}
        onClick={handleClosePopup}
        onLoad={handleLoad}
      >
        {locations.map((location, index) => {
          const lat = location.latitude ?? -16.9176;
          const lng = location.longitude ?? 145.7783;

          if (isNaN(lat) || isNaN(lng)) {
            console.warn(`Invalid coordinates for location at index ${index}:`, location);
            return null;
          }

          return (
            <Marker
              key={index}
              position={{ lat, lng }}
              onClick={() => handleMarkerClick(location)}
              options={{
                clickable: true,
                cursor: 'pointer',
              }}
            />
          );
        })}
      </GoogleMap>

      {/* Popup Info Box */}
      {selectedLocation && popupPosition && (
        <div
          className="absolute bg-white p-4 shadow-lg rounded-lg w-72 z-20"
          style={{
            left: `${popupPosition.x}px`,
            top: `${popupPosition.y}px`,
            transform: 'translate(-50%, -100%) translateY(-20px)',
          }}
        >
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-semibold text-blue-500 text-start border-b-2 w-full border-[#EEF7FF] p-2">
              {selectedLocation.pickup || 'N/A'}
            </h4>
            <button
              className="text-gray-500 hover:text-gray-800 font-bold text-lg"
              onClick={handleClosePopup}
            >
              &times;
            </button>
          </div>
          <div className="flex justify-between items-center gap-4 mt-4">
            <div className="text-center">
              <p className="text-xs text-blue-500">Max</p>
              <p className="font-bold text-gray-800">{selectedLocation.paxNo}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-blue-500">Pickup</p>
              <p className="font-bold text-gray-800">{selectedLocation.paxNo}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-blue-500">Dropoff</p>
              <p className="font-bold text-gray-800">
                {selectedLocation.pickup?.toLowerCase() ===
                selectedLocation.dropOff?.toLowerCase()
                  ? selectedLocation.paxNo
                  : 0}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  </>
);  
};

export default GoogleMapComponent;
